import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { 
  X, User, Mail, Phone, MapPin, Globe, MessageSquare, 
  Calendar, Clock, FileText, CheckCircle, Loader2, AlertCircle 
} from 'lucide-react';
import { toast } from 'sonner';

const BookingModal = ({ isOpen, onClose, sessionType = '', sourcePage = '' }) => {
  const { t, isRTL } = useLanguage();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    mobile: '',
    country: '',
    city: '',
    preferred_language: isRTL ? 'ar' : 'en',
    contact_method: 'whatsapp',
    session_type: sessionType || '',
    preferred_date: '',
    preferred_time: '',
    alternative_date: '',
    alternative_time: '',
    notes: '',
    consent: false,
    source_page: sourcePage || window.location.pathname
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (sessionType) {
      setFormData(prev => ({ ...prev, session_type: sessionType }));
    }
  }, [sessionType]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const [serviceOptions, setServiceOptions] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      setServicesLoading(true);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const [sessionsRes, programsRes] = await Promise.allSettled([
          fetch(`${backendUrl}/api/cms/sessions`),
          fetch(`${backendUrl}/api/cms/programs`)
        ]);

        const options = [];
        const seen = new Set();

        if (sessionsRes.status === 'fulfilled' && sessionsRes.value.ok) {
          const sessions = await sessionsRes.value.json();
          sessions.forEach(s => {
            const val = s.session_type || s.slug;
            if (!seen.has(val)) {
              seen.add(val);
              options.push({
                value: val,
                label: isRTL ? (s.arabic_title || s.title_ar || s.title) : s.title,
                group: 'session'
              });
            }
          });
        }

        if (programsRes.status === 'fulfilled' && programsRes.value.ok) {
          const programs = await programsRes.value.json();
          programs.forEach(p => {
            const val = p.slug;
            if (val && !seen.has(val)) {
              seen.add(val);
              options.push({
                value: val,
                label: isRTL ? (p.arabic_title || p.title) : p.title,
                group: 'program'
              });
            }
          });
        }

        if (options.length > 0) {
          setServiceOptions(options);
        }
      } catch (err) {
        console.error('Error fetching services:', err);
      } finally {
        setServicesLoading(false);
      }
    };
    fetchServices();
  }, [isRTL]);

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00', '18:00'
  ];

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = isRTL ? 'الاسم مطلوب' : 'Full name is required';
    if (!formData.email.trim()) newErrors.email = isRTL ? 'البريد الإلكتروني مطلوب' : 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = isRTL ? 'بريد إلكتروني غير صالح' : 'Invalid email format';
    if (!formData.mobile.trim()) newErrors.mobile = isRTL ? 'رقم الهاتف مطلوب' : 'Mobile number is required';
    if (!formData.country.trim()) newErrors.country = isRTL ? 'الدولة مطلوبة' : 'Country is required';
    if (!formData.city.trim()) newErrors.city = isRTL ? 'المدينة مطلوبة' : 'City is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.session_type) newErrors.session_type = isRTL ? 'يرجى اختيار خدمة' : 'Please select a service';
    if (!formData.preferred_date) newErrors.preferred_date = isRTL ? 'التاريخ المفضل مطلوب' : 'Preferred date is required';
    if (!formData.preferred_time) newErrors.preferred_time = isRTL ? 'الوقت المفضل مطلوب' : 'Preferred time is required';
    if (!formData.consent) newErrors.consent = isRTL ? 'الموافقة مطلوبة' : 'Consent is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setIsSubmitting(true);
    setError('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        // Check status BEFORE reading the body to avoid "body stream already read" errors.
        if (response.status === 409) {
          const friendlyMessage = isRTL
            ? 'هذا الموعد محجوز بالفعل. يرجى اختيار تاريخ أو وقت مختلف.'
            : 'This time slot is already booked. Please choose a different date or time.';
          setError(friendlyMessage);
          // Highlight the conflicting date + time fields with a red border
          setErrors(prev => ({ ...prev, preferred_date: ' ', preferred_time: ' ' }));
          toast.error(friendlyMessage);
          // Scroll the error banner into view within the modal's own scroll container
          setTimeout(() => {
            const banner = document.getElementById('booking-error-message');
            if (banner) {
              banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 50);
          return;
        }
        // For all other non-ok responses, safely attempt to parse the body
        let errorMessage = isRTL ? 'حدث خطأ. يرجى المحاولة مرة أخرى.' : 'An error occurred. Please try again.';
        try {
          const errorData = await response.json();
          if (errorData?.detail) errorMessage = errorData.detail;
        } catch {
          // Non-JSON response — keep the generic fallback message
        }
        throw new Error(errorMessage);
      }

      setIsSubmitted(true);
      toast.success(isRTL ? 'تم إرسال طلبك بنجاح!' : 'Your booking request has been submitted!');
    } catch (err) {
      clearTimeout(timeoutId);
      const isTimeout = err.name === 'AbortError';
      const msg = isTimeout
        ? (isRTL ? 'انتهت مهلة الطلب. يرجى التحقق من اتصالك والمحاولة مرة أخرى.' : 'Request timed out. Please check your connection and try again.')
        : (err.message || (isRTL ? 'حدث خطأ. يرجى المحاولة مرة أخرى.' : 'An error occurred. Please try again.'));
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setIsSubmitted(false);
    setError('');
    setFormData({
      full_name: '',
      email: '',
      mobile: '',
      country: '',
      city: '',
      preferred_language: isRTL ? 'ar' : 'en',
      contact_method: 'whatsapp',
      session_type: sessionType || '',
      preferred_date: '',
      preferred_time: '',
      alternative_date: '',
      alternative_time: '',
      notes: '',
      consent: false,
      source_page: sourcePage || window.location.pathname
    });
    onClose();
  };

  if (!isOpen) return null;

  const minDate = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-charcoal/95 sm:bg-charcoal/90 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal - Full screen on mobile */}
      <div className="relative w-full max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto bg-charcoal-100 border-0 sm:border border-gold/20 safe-area-bottom" style={{ WebkitOverflowScrolling: 'touch' }}>
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 sm:p-6 bg-charcoal border-b border-gold/20 safe-area-top">
          <div>
            <p className="text-gold text-xs uppercase tracking-[0.2em] mb-1">
              {isRTL ? 'احجز جلستك' : 'Book Your Session'}
            </p>
            <h2 className="font-serif text-xl sm:text-2xl text-cream">
              {isRTL ? 'سجل الآن' : 'Register Now'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-3 -mr-2 text-cream-400 hover:text-gold transition-colors active:scale-95"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Success State */}
        {isSubmitted ? (
          <div className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-gold/20 rounded-full flex items-center justify-center">
              <CheckCircle size={40} className="text-gold" />
            </div>
            <h3 className="font-serif text-2xl text-gold mb-4">
              {isRTL ? 'تم استلام طلبك!' : 'Request Received!'}
            </h3>
            <p className="text-cream-300 mb-6">
              {isRTL 
                ? 'شكراً لتسجيلك. سنتواصل معك قريباً لتأكيد جلستك.'
                : 'Thank you for registering. We will contact you shortly to confirm your session.'}
            </p>
            <div className="p-4 bg-charcoal border border-gold/20 text-left mb-6">
              <h4 className="text-gold text-sm uppercase tracking-wider mb-3">
                {isRTL ? 'ملخص الحجز' : 'Booking Summary'}
              </h4>
              <div className="space-y-2 text-sm">
                <p className="text-cream-300"><span className="text-cream">{isRTL ? 'الاسم:' : 'Name:'}</span> {formData.full_name}</p>
                <p className="text-cream-300"><span className="text-cream">{isRTL ? 'الخدمة:' : 'Service:'}</span> {serviceOptions.find(s => s.value === formData.session_type)?.label || formData.session_type}</p>
                <p className="text-cream-300"><span className="text-cream">{isRTL ? 'التاريخ:' : 'Date:'}</span> {formData.preferred_date}</p>
                <p className="text-cream-300"><span className="text-cream">{isRTL ? 'الوقت:' : 'Time:'}</span> {formData.preferred_time}</p>
              </div>
            </div>
            <button onClick={handleClose} className="btn-gold">
              {isRTL ? 'إغلاق' : 'Close'}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-4 p-4 border-b border-white/10">
              <div className={`flex items-center gap-2 ${step >= 1 ? 'text-gold' : 'text-cream-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-gold text-charcoal' : 'bg-charcoal-100 border border-white/20'}`}>1</div>
                <span className="text-sm hidden sm:inline">{isRTL ? 'المعلومات الشخصية' : 'Personal Info'}</span>
              </div>
              <div className={`w-12 h-px ${step >= 2 ? 'bg-gold' : 'bg-white/20'}`} />
              <div className={`flex items-center gap-2 ${step >= 2 ? 'text-gold' : 'text-cream-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-gold text-charcoal' : 'bg-charcoal-100 border border-white/20'}`}>2</div>
                <span className="text-sm hidden sm:inline">{isRTL ? 'تفاصيل الجلسة' : 'Session Details'}</span>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div id="booking-error-message" className="mx-6 mt-4 p-4 bg-red-900/40 border border-red-500/60 rounded-lg flex items-center gap-3">
                <AlertCircle size={20} className="text-red-400" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            {/* Step 1: Personal Information */}
            {step === 1 && (
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                {/* Full Name */}
                <div>
                  <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                    <User size={14} className="inline mr-2" />
                    {isRTL ? 'الاسم الكامل' : 'Full Name'} *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    autoComplete="name"
                    className={`w-full px-4 py-3 sm:py-3 bg-charcoal border ${errors.full_name ? 'border-red-500' : 'border-white/10'} text-cream text-base focus:border-gold transition-colors`}
                    placeholder={isRTL ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                  />
                  {errors.full_name && <p className="text-red-400 text-xs mt-1">{errors.full_name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                    <Mail size={14} className="inline mr-2" />
                    {isRTL ? 'البريد الإلكتروني' : 'Email'} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    className={`w-full px-4 py-3 sm:py-3 bg-charcoal border ${errors.email ? 'border-red-500' : 'border-white/10'} text-cream text-base focus:border-gold transition-colors`}
                    placeholder={isRTL ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>

                {/* Mobile */}
                <div>
                  <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                    <Phone size={14} className="inline mr-2" />
                    {isRTL ? 'رقم الهاتف' : 'Mobile Number'} *
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    autoComplete="tel"
                    className={`w-full px-4 py-3 sm:py-3 bg-charcoal border ${errors.mobile ? 'border-red-500' : 'border-white/10'} text-cream text-base focus:border-gold transition-colors`}
                    placeholder={isRTL ? 'أدخل رقم هاتفك' : 'Enter your mobile number'}
                  />
                  {errors.mobile && <p className="text-red-400 text-xs mt-1">{errors.mobile}</p>}
                </div>

                {/* Country & City - Stack on mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                      <Globe size={14} className="inline mr-2" />
                      {isRTL ? 'الدولة' : 'Country'} *
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-charcoal border ${errors.country ? 'border-red-500' : 'border-white/10'} text-cream focus:border-gold transition-colors`}
                      placeholder={isRTL ? 'الدولة' : 'Country'}
                    />
                    {errors.country && <p className="text-red-400 text-xs mt-1">{errors.country}</p>}
                  </div>
                  <div>
                    <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                      <MapPin size={14} className="inline mr-2" />
                      {isRTL ? 'المدينة' : 'City'} *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      autoComplete="address-level2"
                      className={`w-full px-4 py-3 bg-charcoal border ${errors.city ? 'border-red-500' : 'border-white/10'} text-cream text-base focus:border-gold transition-colors`}
                      placeholder={isRTL ? 'المدينة' : 'City'}
                    />
                    {errors.city && <p className="text-red-400 text-xs mt-1">{errors.city}</p>}
                  </div>
                </div>

                {/* Preferred Language & Contact Method - Stack on mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                      {isRTL ? 'اللغة المفضلة' : 'Preferred Language'}
                    </label>
                    <select
                      name="preferred_language"
                      value={formData.preferred_language}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-charcoal border border-white/10 text-cream text-base focus:border-gold transition-colors cursor-pointer"
                    >
                      <option value="en">English</option>
                      <option value="ar">العربية</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                      <MessageSquare size={14} className="inline mr-2" />
                      {isRTL ? 'طريقة التواصل' : 'Contact Method'}
                    </label>
                    <select
                      name="contact_method"
                      value={formData.contact_method}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-charcoal border border-white/10 text-cream text-base focus:border-gold transition-colors cursor-pointer"
                    >
                      <option value="whatsapp">WhatsApp</option>
                      <option value="phone">{isRTL ? 'هاتف' : 'Phone'}</option>
                      <option value="email">{isRTL ? 'بريد إلكتروني' : 'Email'}</option>
                    </select>
                  </div>
                </div>

                {/* Next Button */}
                <div className="pt-4 pb-4 sm:pb-0">
                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-full btn-gold"
                  >
                    {isRTL ? 'التالي' : 'Next'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Session Details */}
            {step === 2 && (
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                {/* Service / Program Type */}
                <div>
                  <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                    {isRTL ? 'الخدمة / البرنامج' : 'Service / Program'} *
                  </label>
                  <select
                    name="session_type"
                    value={formData.session_type}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-charcoal border ${errors.session_type ? 'border-red-500' : 'border-white/10'} text-cream text-base focus:border-gold transition-colors cursor-pointer`}
                  >
                    <option value="" disabled>{servicesLoading ? (isRTL ? 'جاري التحميل...' : 'Loading...') : (isRTL ? 'اختر خدمة...' : 'Select a service...')}</option>
                    {serviceOptions.filter(o => o.group === 'session').length > 0 && (
                      <optgroup label={isRTL ? 'الجلسات' : 'Sessions'}>
                        {serviceOptions.filter(o => o.group === 'session').map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </optgroup>
                    )}
                    {serviceOptions.filter(o => o.group === 'program').length > 0 && (
                      <optgroup label={isRTL ? 'البرامج' : 'Programs'}>
                        {serviceOptions.filter(o => o.group === 'program').map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {errors.session_type && <p className="text-red-400 text-xs mt-1">{errors.session_type}</p>}
                </div>

                {/* Preferred Date & Time - Stack on mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                      <Calendar size={14} className="inline mr-2" />
                      {isRTL ? 'التاريخ المفضل' : 'Preferred Date'} *
                    </label>
                    <input
                      type="date"
                      name="preferred_date"
                      value={formData.preferred_date}
                      onChange={handleChange}
                      min={minDate}
                      className={`w-full px-4 py-3 bg-charcoal border ${errors.preferred_date ? 'border-red-500' : 'border-white/10'} text-cream text-base focus:border-gold transition-colors`}
                    />
                    {errors.preferred_date && <p className="text-red-400 text-xs mt-1">{errors.preferred_date}</p>}
                  </div>
                  <div>
                    <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                      <Clock size={14} className="inline mr-2" />
                      {isRTL ? 'الوقت المفضل' : 'Preferred Time'} *
                    </label>
                    <select
                      name="preferred_time"
                      value={formData.preferred_time}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-charcoal border ${errors.preferred_time ? 'border-red-500' : 'border-white/10'} text-cream text-base focus:border-gold transition-colors cursor-pointer`}
                    >
                      <option value="">{isRTL ? 'اختر الوقت' : 'Select time'}</option>
                      {timeSlots.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                    {errors.preferred_time && <p className="text-red-400 text-xs mt-1">{errors.preferred_time}</p>}
                  </div>
                </div>

                {/* Alternative Date & Time - Stack on mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                      {isRTL ? 'تاريخ بديل' : 'Alternative Date'}
                    </label>
                    <input
                      type="date"
                      name="alternative_date"
                      value={formData.alternative_date}
                      onChange={handleChange}
                      min={minDate}
                      className="w-full px-4 py-3 bg-charcoal border border-white/10 text-cream focus:border-gold transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                      {isRTL ? 'وقت بديل' : 'Alternative Time'}
                    </label>
                    <select
                      name="alternative_time"
                      value={formData.alternative_time}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-charcoal border border-white/10 text-cream focus:border-gold transition-colors cursor-pointer"
                    >
                      <option value="">{isRTL ? 'اختر الوقت' : 'Select time'}</option>
                      {timeSlots.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-cream-400 text-xs uppercase tracking-wider mb-2">
                    <FileText size={14} className="inline mr-2" />
                    {isRTL ? 'ملاحظات إضافية' : 'Additional Notes'}
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-charcoal border border-white/10 text-cream focus:border-gold transition-colors resize-none"
                    placeholder={isRTL ? 'أي معلومات إضافية تود مشاركتها...' : 'Any additional information you would like to share...'}
                  />
                </div>

                {/* Consent */}
                <div className={`p-4 border ${errors.consent ? 'border-red-500 bg-red-900/10' : 'border-white/10'}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleChange}
                      className="mt-1 w-5 h-5 accent-gold cursor-pointer"
                    />
                    <span className="text-cream-300 text-sm">
                      {isRTL 
                        ? 'أوافق على أن يتم التواصل معي بخصوص طلب الحجز هذا وأفهم أن معلوماتي ستُعامل بسرية.'
                        : 'I consent to being contacted regarding this booking request and understand that my information will be handled confidentially.'}
                    </span>
                  </label>
                  {errors.consent && <p className="text-red-400 text-xs mt-2">{errors.consent}</p>}
                </div>

                {/* Buttons */}
                <div className="flex flex-col gap-3 pt-4">
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex-1 btn-outline-gold"
                    >
                      {isRTL ? 'السابق' : 'Back'}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 btn-gold flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          <span>{isRTL ? 'جاري الإرسال...' : 'Submitting...'}</span>
                        </>
                      ) : (
                        <span>{isRTL ? 'إرسال الطلب' : 'Confirm Booking'}</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
